// The graph as a drifting field of words.
//
// No boxes: a node IS its title, set in sky. Size is the importance score (how many
// nodes transitively depend on it; see crud_graph.importance_map), so the
// load-bearing ideas read large and the leaves read small.
//
// Nothing is permanent. A limited number of titles share the field, and each
// one fades in, drifts, and fades out again, replaced by another — so the whole
// graph passes through over time instead of being crammed in at once.
// Round-robin, not random: every node takes its turn before any node takes a
// second one.
//
// Vertical position is still the dependency layer, so the downward reading of
// the graph survives the motion; only the horizontal drift is free.
//
// Hover or click a title and its own edges are drawn, with whatever it connects
// to pulled onto the field and HELD there — otherwise you could be reading a
// node whose neighbour fades out exactly when it matters.
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

const PAD = 26;
const MIN_FONT = 11;
const MAX_FONT = 21;
const DRIFT = 6; // px per second
const GAP = 22; // clear air between two titles sharing a line
const LIFETIME = [11000, 19000]; // ms on screen before a title is swapped out
const FADE = 1400; // ms of fade at each end
const JITTER = 34; // vertical scatter within a layer band — also keeps
                   // same-layer titles off each other's lines, which is
                   // what gives the separation solver room to work
const CHAR_W = 0.55; // rough advance width per character, in font sizes

const COLOUR = "#7dd3fc"; // sky-300

/** Deterministic pseudo-random in [0,1) — same node, same drift every time. */
function seeded(text, salt) {
  let h = 2166136261 ^ salt;
  for (let i = 0; i < text.length; i += 1) h = Math.imul(h ^ text.charCodeAt(i), 16777619);
  return ((h >>> 0) % 10000) / 10000;
}

export default function GraphFlow({
  layers,
  edges,
  activeSlug = null,
  onSelect = null,
  height = 460,
}) {
  const boxRef = useRef(null);
  const textEls = useRef(new Map());
  const edgeEls = useRef(new Map());
  const motion = useRef(new Map()); // slug -> drift + lifecycle state
  const queue = useRef({ order: [], next: 0 }); // round-robin over every node
  const [width, setWidth] = useState(720);
  const [visible, setVisible] = useState([]); // slugs currently on the field
  // The field's membership is mutated from two places that can run between
  // renders — the focus effect and the animation loop — so `visible` alone is
  // not a safe thing to read. Both read this ref instead and go through
  // setField, which is also the single place that de-duplicates: a slug landing
  // in the list twice renders the same title twice, at two opacities, with two
  // identical React keys.
  const visibleRef = useRef([]);

  const setField = (next) => {
    const deduped = [...new Set(next)];
    visibleRef.current = deduped;
    setVisible(deduped);
  };

  // Only a CLICK shows a node's connections — hovering does nothing.
  //
  // Hover was the obvious gesture and the wrong one here. The titles move, so a
  // parked cursor catches whatever drifts under it and the edges flicker
  // between nodes nobody asked about; and hover is a gesture you make by
  // accident on the way to somewhere else. Showing the connections also freezes
  // the field, which is far too heavy a consequence for a passing pointer.
  const focus = activeSlug;

  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el) return undefined;
    const measure = () => {
      const cs = window.getComputedStyle(el);
      const pad = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight);
      setWidth(Math.max(280, el.clientWidth - pad));
    };
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const { nodes, maxScore, depthCount, neighboursOf } = useMemo(() => {
    const map = new Map();
    layers.forEach((l) => l.nodes.forEach((n) => map.set(n.slug, { ...n, depth: l.depth })));
    const nb = new Map();
    edges.forEach(({ source, target }) => {
      if (!nb.has(source)) nb.set(source, new Set());
      if (!nb.has(target)) nb.set(target, new Set());
      nb.get(source).add(target);
      nb.get(target).add(source);
    });
    return {
      nodes: map,
      neighboursOf: nb,
      maxScore: Math.max(1, ...[...map.values()].map((n) => n.importance || 0)),
      depthCount: Math.max(1, layers.length),
    };
  }, [layers, edges]);

  /** Font size, estimated text width, and layer band for one title. */
  const shapeOf = useMemo(
    () => (slug) => {
      const node = nodes.get(slug);
      if (!node) return null;
      const t = Math.sqrt((node.importance || 0) / maxScore);
      const font = MIN_FONT + (MAX_FONT - MIN_FONT) * t;
      const w = Math.min(width - PAD * 2, node.title.length * font * CHAR_W);
      const band = (height - PAD * 2) / depthCount;
      const y = PAD + band * (node.depth + 0.5) + (seeded(slug, 7) - 0.5) * 2 * JITTER;
      return { node, t, font, w, y };
    },
    [nodes, maxScore, width, height, depthCount],
  );

  // How many titles share the field: enough to feel populated, few enough that
  // two rarely collide. Scaled to the pane so a narrow screen is not crowded.
  const capacity = useMemo(
    () => Math.max(5, Math.min(14, Math.round((width * height) / 34000))),
    [width, height],
  );

  const spawn = useMemo(
    () => (slug, now, immediate) => {
      const shape = shapeOf(slug);
      if (!shape) return;
      const dir = seeded(slug, 1) < 0.5 ? -1 : 1;
      motion.current.set(slug, {
        x: PAD + shape.w / 2 + seeded(slug, 4) * Math.max(1, width - PAD * 2 - shape.w),
        vx: dir * DRIFT * (0.5 + seeded(slug, 2) * 1.1),
        born: immediate ? now - FADE : now,
        ttl: LIFETIME[0] + seeded(slug, 5) * (LIFETIME[1] - LIFETIME[0]),
        w: shape.w,
        y: shape.y,
        bob: 0,
      });
    },
    [shapeOf, width],
  );

  // Seed the round-robin order (most important first, so the opening frame is
  // the part of the graph most worth seeing) and fill the field.
  useEffect(() => {
    if (!nodes.size) return;
    queue.current.order = [...nodes.values()]
      .sort((a, b) => (b.importance || 0) - (a.importance || 0) || a.slug.localeCompare(b.slug))
      .map((n) => n.slug);
    const now = performance.now();
    const start = queue.current.order.slice(0, capacity);
    start.forEach((slug, i) => spawn(slug, now - i * 240, true));
    queue.current.next = start.length;
    setField(start);
  }, [nodes, capacity, spawn]);

  // Held on the field while focused: the node being read and everything it
  // connects to.
  const pinned = useMemo(
    () => (focus ? new Set([focus, ...(neighboursOf.get(focus) || [])]) : null),
    [focus, neighboursOf],
  );

  const shownEdges = useMemo(() => {
    if (!focus) return [];
    const live = new Set(visible);
    return edges.filter(
      ({ source, target }) =>
        (source === focus || target === focus) && live.has(source) && live.has(target),
    );
  }, [focus, edges, visible]);

  // Lay the pinned set out properly instead of leaving it where the drift
  // happened to have put it.
  //
  // The separation solver can only push along a line, and a well-connected node
  // pulls in more neighbours than one line holds — image_tensor has eight in a
  // single dependency layer. Pushed apart they run off the pane; clamped they
  // overlap. Neither is readable, and no amount of solving invents width.
  //
  // So on focus, the pinned titles are PACKED: grouped by layer, wrapped onto
  // as many rows as the pane needs, and centred. It is a deliberate static
  // arrangement for a deliberately static view.
  const layoutPinned = useMemo(
    () => (slugs) => {
      const rowsByDepth = new Map();
      slugs.forEach((slug) => {
        const shape = shapeOf(slug);
        const m = motion.current.get(slug);
        if (!shape || !m) return;
        if (!rowsByDepth.has(shape.node.depth)) rowsByDepth.set(shape.node.depth, []);
        rowsByDepth.get(shape.node.depth).push({ slug, m, w: shape.w });
      });

      const usable = width - PAD * 2;

      // Wrap each layer onto as many rows as the pane's WIDTH needs.
      const groups = [...rowsByDepth.keys()]
        .sort((a, b) => a - b)
        .map((depth) => {
          const rows = [[]];
          let used = 0;
          rowsByDepth
            .get(depth)
            .sort((a, b) => b.w - a.w)
            .forEach((item) => {
              const need = item.w + (rows[rows.length - 1].length ? GAP : 0);
              if (used + need > usable && rows[rows.length - 1].length) {
                rows.push([]);
                used = item.w;
              } else {
                used += need;
              }
              rows[rows.length - 1].push(item);
            });
          return { depth, rows };
        });

      // Then spend the pane's HEIGHT on the rows that actually exist.
      //
      // The drift view divides the height by EVERY layer in the graph, which is
      // right when any node might float in. Focused, only a handful of layers
      // are on screen, and keeping their original bands crushes the whole
      // picture into the top third and leaves most of the pane empty. So the
      // rows are re-spread over the full height here, with a wider gap between
      // one layer and the next than between two rows of the same layer — the
      // spacing then says which titles are peers.
      const totalRows = groups.reduce((n, g) => n + g.rows.length, 0);
      const layerBreaks = Math.max(0, groups.length - 1);
      const available = height - PAD * 2;
      // A layer break is worth ~1.6 rows of space; solve for the row pitch that
      // fills the pane, then cap it so three titles are not flung to the corners.
      const pitch = Math.min(74, available / (totalRows + layerBreaks * 0.6));
      const blockH = pitch * totalRows + pitch * 0.6 * layerBreaks;
      let y = PAD + Math.max(0, (available - blockH) / 2) + pitch / 2;

      groups.forEach((group, groupIndex) => {
        group.rows.forEach((row) => {
          const rowW = row.reduce((s, i) => s + i.w, 0) + GAP * (row.length - 1);
          let x = PAD + Math.max(0, (usable - rowW) / 2);
          row.forEach((item) => {
            item.m.x = x + item.w / 2;
            item.m.y = y;
            item.m.bob = 0;
            x += item.w + GAP;
          });
          y += pitch;
        });
        if (groupIndex < groups.length - 1) y += pitch * 0.6;
      });
    },
    [shapeOf, width, height],
  );

  // Pull a focused node's neighbours onto the field, replacing the oldest
  // titles that are not themselves pinned.
  useEffect(() => {
    if (!pinned) return;
    const current = visibleRef.current;
    const live = new Set(current);
    const wanted = [...pinned].filter((s) => nodes.has(s));
    const missing = wanted.filter((s) => !live.has(s));

    const now = performance.now();
    missing.forEach((slug, i) => spawn(slug, now - i * 120, false));
    // Pack every pinned title, not only the ones just added — the ones already
    // on the field are wherever the drift left them.
    layoutPinned(wanted);
    if (!missing.length) return;

    const keep = current.filter((s) => pinned.has(s));
    const spare = current
      .filter((s) => !pinned.has(s))
      .sort((a, b) => (motion.current.get(a)?.born || 0) - (motion.current.get(b)?.born || 0));
    const room = Math.max(0, Math.max(capacity, pinned.size) - keep.length - missing.length);
    setField([...keep, ...spare.slice(spare.length - room), ...missing]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinned, nodes, capacity, spawn, layoutPinned]);

  // The animation. Positions and opacities are written onto the DOM directly —
  // this runs every frame, and re-rendering through React at 60fps to move a
  // word a few pixels would be the most expensive thing on the page. Only
  // MEMBERSHIP goes through state, and that changes every few seconds.
  useEffect(() => {
    const reduced =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    // The field FREEZES while a node is being shown. Reading a node and its
    // edges is a static act: the words it connects to must stay where they are
    // long enough to be read, and an arrow that keeps re-aiming at a moving
    // target is harder to follow than a still one. Drift is for the idle view,
    // where the motion is what surfaces the rest of the graph.
    const frozen = Boolean(focus);

    let frame = 0;
    let last = 0;
    const step = (now) => {
      const dt = last ? Math.min((now - last) / 1000, 0.05) : 0;
      last = now;
      const retiring = [];

      visible.forEach((slug) => {
        const m = motion.current.get(slug);
        const el = textEls.current.get(slug);
        if (!m || !el) return;

        if (!reduced && !frozen) {
          m.x += m.vx * dt;
          const half = m.w / 2;
          if (m.x - half < PAD) {
            m.x = PAD + half;
            m.vx = Math.abs(m.vx);
          } else if (m.x + half > width - PAD) {
            m.x = width - PAD - half;
            m.vx = -Math.abs(m.vx);
          }
        }

        const held = pinned ? pinned.has(slug) : false;
        // A held title keeps its clock pushed back so it never reaches the fade.
        if (held) m.born = Math.max(m.born, now - m.ttl + FADE * 2);
        const age = now - m.born;
        // Frozen: fade IN still runs, so a neighbour pulled onto the field
        // appears rather than popping; nothing fades out and nothing retires.
        let alpha = Math.min(1, age / FADE);
        if (!frozen && !held && age > m.ttl - FADE) {
          alpha = Math.max(0, (m.ttl - age) / FADE);
        }
        if (!frozen && !held && age > m.ttl) retiring.push(slug);

        // The vertical bob is remembered, so freezing holds the exact position
        // the eye last saw instead of snapping to the band centre.
        if (!frozen) m.bob = reduced ? 0 : Math.sin(now / 1000 + m.phase) * 3;
        const dim = pinned && !pinned.has(slug) ? 0.2 : 1;
        el.setAttribute("transform", `translate(${m.x}, ${m.y + (m.bob || 0)})`);
        el.setAttribute("opacity", String(alpha * dim));
        m.alpha = alpha;
      });

      // Keep titles sharing a line off each other. Compared pairwise on the
      // lines they actually occupy, not on layer membership — the vertical
      // jitter means two nodes of one layer often do not share a line at all.
      // Separation runs even when frozen. Freezing stops the DRIFT, not the
      // solver: pulling a focused node's neighbours onto the field places them
      // wherever they happen to land, and with the solver off they simply stay
      // on top of each other. Pushing apart from a standstill settles in a few
      // frames and then nothing moves — which is the static picture wanted.
      const live = visible
        .map((slug) => motion.current.get(slug))
        .filter(Boolean)
        .sort((a, b) => a.x - b.x);
      for (let i = 0; i < live.length; i += 1) {
        for (let j = i + 1; j < live.length; j += 1) {
          const a = live[i];
          const b = live[j];
          if (Math.abs(a.y - b.y) > 18) continue;
          const overlap = a.x + a.w / 2 + GAP - (b.x - b.w / 2);
          if (overlap <= 0) continue;
          a.x -= overlap / 2;
          b.x += overlap / 2;
          if (a.vx > b.vx) {
            a.vx = -Math.abs(a.vx);
            b.vx = Math.abs(b.vx);
          }
        }
      }

      // Clamp AFTER separating. The solver pushes outward and knows nothing
      // about the pane, so on a crowded line it will happily shove a title off
      // the edge — which is worse than the overlap it was fixing, because a
      // clipped word cannot be read at all. Runs frozen or not: the frozen view
      // is exactly where crowding happens, since focusing a well-connected node
      // pulls all of its neighbours in at once.
      visible.forEach((slug) => {
        const m = motion.current.get(slug);
        const el = textEls.current.get(slug);
        if (!m || !el) return;
        const half = m.w / 2;
        const clamped = Math.max(PAD + half, Math.min(m.x, width - PAD - half));
        if (clamped !== m.x) {
          m.x = clamped;
          el.setAttribute("transform", `translate(${m.x}, ${m.y + (m.bob || 0)})`);
        }
      });

      shownEdges.forEach(({ source, target }) => {
        const path = edgeEls.current.get(`${source}->${target}`);
        const a = motion.current.get(source);
        const b = motion.current.get(target);
        if (!path || !a || !b) return;
        const bend = Math.min(36, Math.abs(b.y - a.y) / 2);
        path.setAttribute(
          "d",
          `M ${a.x} ${a.y + 6} C ${a.x} ${a.y + 6 + bend}, ${b.x} ${b.y - 16 - bend}, ${b.x} ${b.y - 16}`,
        );
        path.setAttribute("opacity", String(Math.min(a.alpha ?? 1, b.alpha ?? 1)));
      });

      if (retiring.length) {
        const q = queue.current;
        const next = visibleRef.current.filter((s) => !retiring.includes(s));
        const taken = new Set(next);
        retiring.forEach(() => {
          // Round-robin to the next node not already on the field, so every
          // title appears before any title repeats.
          for (let tries = 0; tries < q.order.length; tries += 1) {
            const slug = q.order[q.next % q.order.length];
            q.next += 1;
            if (!taken.has(slug)) {
              spawn(slug, performance.now(), false);
              taken.add(slug);
              next.push(slug);
              return;
            }
          }
        });
        setField(next);
      }

      frame = window.requestAnimationFrame(step);
    };
    frame = window.requestAnimationFrame(step);
    return () => window.cancelAnimationFrame(frame);
  }, [visible, shownEdges, pinned, focus, width, spawn]);

  return (
    <div
      ref={boxRef}
      className="overflow-hidden rounded-xl border border-slate-800 bg-slate-900/40 p-2"
    >
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label="Knowledge graph: titles drift by, sized by how much depends on them"
      >
        <defs>
          <marker
            id="kgf-arrow"
            viewBox="0 0 8 8"
            refX="7"
            refY="4"
            markerWidth="6"
            markerHeight="6"
            orient="auto"
          >
            <path d="M0,0 L8,4 L0,8 z" fill="#38bdf8" />
          </marker>
        </defs>

        {shownEdges.map(({ source, target }) => (
          <path
            key={`${source}->${target}`}
            ref={(el) => {
              if (el) edgeEls.current.set(`${source}->${target}`, el);
              else edgeEls.current.delete(`${source}->${target}`);
            }}
            fill="none"
            stroke="#38bdf8"
            strokeWidth={1.5}
            markerEnd="url(#kgf-arrow)"
          />
        ))}

        {visible.map((slug) => {
          const shape = shapeOf(slug);
          if (!shape) return null;
          const isActive = slug === activeSlug;
          return (
            <text
              key={slug}
              ref={(el) => {
                if (el) textEls.current.set(slug, el);
                else textEls.current.delete(slug);
              }}
              textAnchor="middle"
              fontSize={shape.font}
              fontFamily="ui-sans-serif, system-ui, sans-serif"
              fontWeight={isActive ? 600 : 400}
              fill={isActive ? "#f1f5f9" : COLOUR}
              opacity={0}
              style={{ cursor: onSelect ? "pointer" : "default" }}
              onClick={() => onSelect && onSelect(slug)}
            >
              <title>
                {shape.node.title}
                {" — "}
                {shape.node.importance || 0} node
                {(shape.node.importance || 0) === 1 ? "" : "s"} depend on it
              </title>
              {shape.node.title}
            </text>
          );
        })}
      </svg>
    </div>
  );
}
